/* eslint-disable react/jsx-no-bind */

import { memo, useCallback, useEffect, useMemo, useState } from "react";
import Icon from "~/components/shared/Icon";
import cn from "classnames";
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from "~/components/shared/PopoverNext";

import {
  isGitHubService,
  isRestrictedAppService,
  SCMProviderType,
  type SCMServiceInfo,
  type ManualProviderType,
} from "~/components/pipeline/New/ConnectGitPicker/util/types";
import { openWindow } from "~/components/pipeline/New/ConnectGitPicker/util/openWindow";

const POPUP_WIDTH = 800;
const POPUP_HEIGHT = 600;

type GitProviderOption = {
  icon: string;
  title: string;
  description: string;
  backgroundColor: string;
  textColor: string;
};

const SCM_PROVIDER_TYPE_ICON_MAP = {
  [SCMProviderType.Git]: "custom/solid/16x/git",
  [SCMProviderType.GithubApp]: "custom/solid/16x/github-limited",
  [SCMProviderType.GithubCodeAccessApp]: "custom/solid/16x/github",
  [SCMProviderType.GithubEnterpriseApp]: "custom/solid/16x/github-limited", // Add support for the Restricted GitHub App
} as const;

const MANUAL_PROVIDER_CONFIG = {
  git: {
    displayText: "Any account",
    iconName: "custom/solid/16x/git",
    backgroundColor: "bg-[#F0EFE7]",
    iconColor: "text-[#F05133]",
  },
  gitlab: {
    displayText: "GitLab",
    iconName: "custom/solid/16x/gitlab",
    backgroundColor: "bg-[#232150]",
    iconColor: "text-[#FC6D26]",
  },
  bitbucket: {
    displayText: "Bitbucket",
    iconName: "custom/solid/16x/bitbucket",
    backgroundColor: "bg-[#94C748]",
    iconColor: "text-[#101214]",
  },
} as const;

const GIT_PROVIDERS: Record<string, GitProviderOption> = {
  github: {
    title: "GitHub",
    description: "Easily connect your repositories",
    backgroundColor: "bg-[#24292f]",
    textColor: "text-white",
    icon: "custom/solid/16x/github",
  },
};

type GitProviderPickerProps = {
  services?: Array<SCMServiceInfo> | null;
  selectedService: SCMServiceInfo | null;
  selectedManualProvider: ManualProviderType;
  onServiceChange: (
    service: SCMServiceInfo | null,
    manualProviderType?: ManualProviderType,
  ) => void;
  connectAccountURL?: string;
  connectGitHubAccountURL?: string;
  className?: string;
  avatarCache?: {
    getAvatarState: (service: SCMServiceInfo) => { preloaded: boolean; error: boolean };
  };
};

const ServiceAvatar = memo(
  ({
    service,
    avatarState,
    className = "size-4",
    showLockIcon = false,
  }: {
    service: SCMServiceInfo;
    avatarState: { preloaded: boolean; error: boolean };
    className?: string;
    showLockIcon?: boolean;
  }) => {
    const [imageError, setImageError] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);

    const isPreloaded = avatarState.preloaded || imageLoaded;
    const shouldShowIcon = !service.avatar_url || avatarState.error || imageError;

    if (shouldShowIcon) {
      return (
        <Icon icon={SCM_PROVIDER_TYPE_ICON_MAP[service.provider_type]} className={className} />
      );
    }

    return (
      <div className="relative flex">
        {!isPreloaded && <div className="absolute inset-0 animate-pulse rounded bg-gray-400" />}
        <img
          src={service.avatar_url}
          alt={`${service.account_login} avatar`}
          className={cn("size-5 flex-shrink-0 rounded", isPreloaded ? "opacity-100" : "opacity-0")}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
          loading="eager"
        />
        {showLockIcon && (
          <Icon
            icon="custom/solid/16x/lock"
            style={{ paintOrder: "stroke" }}
            className="absolute -bottom-0.5 -right-1 size-3.5 stroke-white stroke-[4] text-purple-600"
          />
        )}
      </div>
    );
  },
);

// Memoized service button to prevent unnecessary re-renders
const ServiceButton = memo(
  ({
    service,
    isSelected,
    onClick,
    avatarState,
  }: {
    service: SCMServiceInfo;
    isSelected: boolean;
    onClick: () => void;
    avatarState: { preloaded: boolean; error: boolean };
  }) => (
    <button
      key={service.uuid}
      className={cn([
        "flex w-full cursor-pointer items-center gap-2 rounded px-2 py-2 hover:bg-gray-200",
        isSelected && "!bg-purple-100 !text-purple-600 hover:ring-1 hover:ring-purple-300",
      ])}
      onClick={onClick}
    >
      <ServiceAvatar service={service} avatarState={avatarState} className="size-5" />
      <span className="truncate text-left" title={service.account_login}>
        {service.account_login}
      </span>
      <span
        className={cn([
          "dark-gray ml-auto w-fit text-nowrap text-right text-xs italic",
          isSelected && "text-purple-600",
        ])}
      >
        {isGitHubService(service) && !service.has_code_access && "Limited Access"}
        {!isGitHubService(service) &&
          !service.has_code_access &&
          isRestrictedAppService(service) &&
          "Restricted"}
      </span>
    </button>
  ),
);

export const GitProviderPicker = memo(function GitProviderPicker({
  services = [],
  selectedService,
  selectedManualProvider,
  onServiceChange,
  connectAccountURL,
  connectGitHubAccountURL,
  className,
  avatarCache,
}: GitProviderPickerProps) {
  const [open, setOpen] = useState(false);

  const getAvatarState = useMemo(
    () => avatarCache?.getAvatarState || (() => ({ preloaded: false, error: false })),
    [avatarCache],
  );

  const handleServiceSelect = useCallback(
    (service: SCMServiceInfo | null, manualProviderType?: ManualProviderType) => {
      onServiceChange(service, manualProviderType);
      setOpen(false);
    },
    [onServiceChange],
  );

  const { displayText, iconName, selectedServiceAvatarState } = useMemo(() => {
    if (selectedService) {
      return {
        displayText: selectedService.account_login,
        iconName: SCM_PROVIDER_TYPE_ICON_MAP[selectedService.provider_type],
        selectedServiceAvatarState: selectedService ? getAvatarState(selectedService) : null,
      };
    }

    const config = MANUAL_PROVIDER_CONFIG[selectedManualProvider];
    return {
      displayText: config.displayText,
      iconName: config.iconName,
      selectedServiceAvatarState: null,
    };
  }, [selectedService, selectedManualProvider, getAvatarState]);

  useEffect(() => {
    const channel = new BroadcastChannel("github_app_install");
    channel.onmessage = (event) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data.status === "success" || event.data.status === "failed") {
        const frame = document.querySelector<HTMLTurboFrame>("#git_repository_selector");

        if (frame) {
          frame.src = window.location.href;
          frame.reload();
        } else {
          window.location.reload();
        }
      }
    };

    return () => {
      channel.close();
    };
  }, []);

  const openConnectPopup = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
    event.preventDefault();

    openWindow(event.currentTarget.href, "Connect account", {
      width: POPUP_WIDTH,
      height: POPUP_HEIGHT,
    });

    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild={true}>
        <button
          data-testid="account-select"
          className={cn(["btn btn-small btn-depth btn-flex w-full gap-2 px-1", className])}
          id="account-select"
        >
          {selectedService ? (
            <ServiceAvatar
              service={selectedService}
              avatarState={selectedServiceAvatarState || { preloaded: false, error: false }}
              showLockIcon={isGitHubService(selectedService) && !selectedService.has_code_access}
            />
          ) : (
            <div
              className={cn([
                "flex size-5 items-center justify-center rounded",
                MANUAL_PROVIDER_CONFIG[selectedManualProvider].backgroundColor,
              ])}
            >
              <Icon
                icon={iconName}
                className={cn(["size-4", MANUAL_PROVIDER_CONFIG[selectedManualProvider].iconColor])}
              />
            </div>
          )}
          <span className="truncate text-black">{displayText}</span>
          <Icon icon="custom/outline/chevron-down" className="ml-auto" />
        </button>
      </PopoverTrigger>

      <PopoverPortal>
        <PopoverContent
          alignOffset={0}
          avoidCollisions={false}
          align="start"
          arrow={false}
          data-testid="git-provider-picker-popover"
          className="mt-1 max-h-96 w-auto min-w-72 max-w-96 overflow-y-auto p-0"
        >
          <div className="p-1">
            {services && services?.length > 0 && (
              <>
                {services?.map((service) => (
                  <ServiceButton
                    key={service.uuid}
                    service={service}
                    isSelected={selectedService?.uuid === service.uuid}
                    onClick={() => handleServiceSelect(service)}
                    avatarState={getAvatarState(service)}
                  />
                ))}
                <div className="my-1 border-t border-gray-200" />
              </>
            )}

            <p className="dark-gray m-0 gap-0.5 px-2 pb-1 pt-0">Use remote URL (SSH or HTTPS)</p>
            {/* Manual provider buttons */}
            {(
              Object.keys(MANUAL_PROVIDER_CONFIG) as Array<keyof typeof MANUAL_PROVIDER_CONFIG>
            ).map((providerKey) => {
              const config = MANUAL_PROVIDER_CONFIG[providerKey];
              const isSelected = !selectedService && selectedManualProvider === providerKey;

              return (
                <button
                  key={providerKey}
                  className={cn([
                    "flex w-full cursor-pointer items-center gap-2 rounded px-2 py-2 hover:bg-gray-200",
                    isSelected && "!bg-purple-100 !text-purple-600",
                  ])}
                  onClick={() => handleServiceSelect(null, providerKey)}
                >
                  <div
                    className={cn([
                      "flex size-5 items-center justify-center rounded",
                      config.backgroundColor,
                    ])}
                  >
                    <Icon icon={config.iconName} className={cn(["size-4", config.iconColor])} />
                  </div>
                  <span className="-ml-px text-left">{config.displayText}</span>
                </button>
              );
            })}

            {(connectGitHubAccountURL || connectAccountURL) && (
              <div className="my-1 border-t border-gray-200" />
            )}

            {connectGitHubAccountURL && (
              <a
                href={connectGitHubAccountURL}
                className="flex w-full items-center gap-2 rounded px-2 py-2 text-inherit hover:bg-gray-200 hover:text-inherit"
                onClick={Features.GithubInstallPopup ? openConnectPopup : undefined}
              >
                <div
                  className={cn([
                    "flex size-5 items-center justify-center rounded",
                    GIT_PROVIDERS.github.backgroundColor,
                  ])}
                >
                  <Icon
                    icon={GIT_PROVIDERS.github.icon}
                    className={cn(["size-4", GIT_PROVIDERS.github.textColor])}
                  />
                </div>
                <span>Connect GitHub account</span>
                <Icon icon="custom/outline/chevron-right" className="ml-auto" aria-hidden="true" />
              </a>
            )}

            {/* Add account button */}
            {connectAccountURL && (
              <a
                href={connectAccountURL}
                className="flex w-full items-center gap-2 rounded px-2 py-2 text-inherit hover:bg-gray-200 hover:text-inherit"
              >
                <div className="flex size-5 items-center justify-center">
                  <Icon
                    icon="heroicons/16/solid/adjustments-horizontal"
                    className="text-charcoal-300 size-4"
                  />
                </div>
                <span>Manage accounts</span>
                <Icon icon="custom/outline/chevron-right" className="ml-auto" aria-hidden="true" />
              </a>
            )}
          </div>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
});
